import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import { toast } from "react-toastify";
// import apiClient from "../../services/axiosClient";
import { useDispatch } from "react-redux";
import { oauthLogin } from "../../redux/slices/authSlice";
import axios from "axios";

const AuthorizeCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(null);

  useEffect(() => {
    const exchangeTokens = async () => {
      try {
        // Get query parameters
        const urlParams = new URLSearchParams(window.location.search);
        const authId = urlParams.get("authorize_id");
        const tokenUrl = urlParams.get("token_url");
        const logoutRedirectUrl = urlParams.get("logout_redirect_url");

        if (!authId || !tokenUrl) {
          setError("Missing required parameters (auth_id or token_url)");
          setStatus("error");
          toast.error("Authentication failed: Missing parameters");
          return;
        }

        if (logoutRedirectUrl) {
          localStorage.setItem("logout_redirect_url", logoutRedirectUrl);
        }

        // Retrieve code_verifier from sessionStorage
        const codeVerifier = sessionStorage.getItem("code_verifier");

        if (!codeVerifier) {
          setError(
            "Code verifier not found. Please restart the authentication process.",
          );
          setStatus("error");
          toast.error("Authentication session expired");
          return;
        }

        // POST to token_url to exchange tokens
        const response = await axios.post(tokenUrl, {
          authorize_id: authId,
          code_verifier: codeVerifier,
        });

        // Extract tokens from response
        const { agentic_access_token } = response.data;

        if (!agentic_access_token) {
          setError("Invalid response from token endpoint");
          setStatus("error");
          toast.error("Authentication failed: Invalid token response");
          return;
        }

        // Store tokens using the same key as your existing login (auth_token)
        localStorage.setItem("agentic_access_token", agentic_access_token);
        // localStorage.setItem("refresh_token", refresh_token);

        // Clear code_verifier from sessionStorage
        sessionStorage.removeItem("code_verifier");

        dispatch(oauthLogin({ agentic_access_token, navigate }))
          .unwrap()
          .then(() => {
            setStatus("success");
            toast.success("Login Successful!");
          })
          .catch((error) => {
            throw new Error(
              error?.data?.detail || "Failed to authenticate with backend",
            );
          });
      } catch (error) {
        let errorMessage = "Failed to complete authentication";

        if (error.response?.status === 401) {
          errorMessage = "Invalid or expired token";
        } else if (error.response?.status === 400) {
          errorMessage =
            error.response?.data?.detail || "Invalid authentication request";
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        setError(errorMessage);
        setStatus("error");
        toast.error(`Authentication failed: ${errorMessage}`);
      }
    };

    exchangeTokens();
  }, [navigate, dispatch]);

  const handleReturnToLogin = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenUrl = urlParams.get("token_url");

    if (tokenUrl?.includes("stage")) {
      window.location.href = "https://stage.agenticace.pharmaace.ai/";
    } else if (tokenUrl?.includes("prod")) {
      window.location.href = "https://prod.agenticace.pharmaace.ai/";
    } else {
      navigate("/login");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: 3,
        padding: "20px",
      }}
    >
      {status === "processing" && (
        <>
          <CircularProgress size={60} />
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Processing authentication...
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please wait while we complete your login.
          </Typography>
        </>
      )}

      {status === "success" && (
        <>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "success.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="h3" color="success.main">
              ✓
            </Typography>
          </Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 500, color: "success.main" }}
          >
            Authentication successful!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Redirecting to dashboard...
          </Typography>
        </>
      )}

      {status === "error" && (
        <>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "error.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="h3" color="error.main">
              ✕
            </Typography>
          </Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 500, color: "error.main" }}
          >
            Authentication failed
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={handleReturnToLogin}
            sx={{ marginTop: 2 }}
          >
            Return to Login
          </Button>
        </>
      )}
    </Box>
  );
};

export default AuthorizeCallback;
